/* eslint-disable react/jsx-key */
import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import "./index.css";
import SearchIcon from "../svgs/icoMoon/icons/searchIconNew/searchIconNew";
import "../pager/pager.css";

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

function GlobalFilter({ globalFilter, setGlobalFilter }) {
  return (
    <div
      className="inputWithIcon"
      style={{ marginBottom: "8px", marginTop: "8px" }}
    >
      <DebouncedInput
        className="body_field"
        style={{ borderRadius: "8px" }}
        type="text"
        value={globalFilter ?? ""}
        onChange={(value) => setGlobalFilter(String(value))}
        placeholder="search..."
        debounce={200}
      />

      <SearchIcon />
    </div>
  );
}

function ReactTable({
  data,
  columns,
  filter = true,
  pagination = true,
  headerColumnClass,
}) {
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  return (
    <>
      {filter && (
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      )}

      <table className="table-container">
        <thead className="table-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={headerColumnClass}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {/* Add a sort direction indicator */}
                  {{
                    asc: <span className="icon-arrow_up" />,
                    desc: <span className="icon-arrow_down" />,
                  }[header.column.getIsSorted()] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="table-body-column">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <div className="pagination">
          <ul>
            <li
              onClick={() => table.setPageIndex(0)}
              className={!table.getCanPreviousPage() ? "blocked" : null}
            >
              FIRST
            </li>
            <li
              onClick={() => table.previousPage()}
              className={!table.getCanPreviousPage() ? "blocked" : null}
            >
              PREVIOUS
            </li>
            <li>{table.getState().pagination.pageIndex + 1}</li>
            <li
              onClick={() => table.nextPage()}
              className={!table.getCanNextPage() ? "blocked" : null}
            >
              NEXT
            </li>
            <li
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              className={!table.getCanNextPage() ? "blocked" : null}
            >
              LAST
            </li>
          </ul>
        </div>
      )}
    </>
  );
}

export default ReactTable;
